import { AlpineComponent } from "alpinejs";
import { PasswordResetService, ResetPasswordParameters } from "../api";

interface SubmissionError {
	errorMessage: string;
}

const minimumPasswordLength = 10;

const toSubmissionError = (errorMessage: string): SubmissionError => ({ errorMessage });

const normalizeErrors = (error: unknown): SubmissionError[] => {
	if (Array.isArray(error)) {
		return error.map((item) => {
			if (typeof item === "string") {
				return toSubmissionError(item);
			}

			if (
				typeof item === "object" &&
				item !== null &&
				"errorMessage" in item &&
				typeof item.errorMessage === "string"
			) {
				return toSubmissionError(item.errorMessage);
			}

			return toSubmissionError("Unknown error");
		});
	}

	if (typeof error === "string") {
		return [toSubmissionError(error)];
	}

	return [toSubmissionError("Unknown error")];
};

type ResetPassword = AlpineComponent<{
	email: string;
	newPassword: string;
	confirmPassword: string;
	loginUrl: string;
	token: string;
	newPasswordRequiredMessage: string;
	confirmPasswordRequiredMessage: string;
	passwordTooShortMessage: string;
	passwordsDoNotMatchMessage: string;
	showErrorMessage: boolean;
	showSuccessMessage: boolean;
	submissionErrors: SubmissionError[];
	resetPassword: () => void;
}>;

export default (
	email: string,
	token: string,
	loginUrl: string,
	newPasswordRequiredMessage: string,
	confirmPasswordRequiredMessage: string,
	passwordTooShortMessage: string,
	passwordsDoNotMatchMessage: string
): ResetPassword => {
	return {
		email: email || "",
		newPassword: "",
		confirmPassword: "",
		loginUrl: loginUrl,
		newPasswordRequiredMessage,
		confirmPasswordRequiredMessage,
		passwordTooShortMessage,
		passwordsDoNotMatchMessage,
		showErrorMessage: false,
		showSuccessMessage: false,
		submissionErrors: [],
		token: token,
		async resetPassword() {
			this.submissionErrors = [];
			this.showErrorMessage = false;
			this.showSuccessMessage = false;

			if (!this.newPassword) {
				this.submissionErrors.push(toSubmissionError(this.newPasswordRequiredMessage));
				this.showErrorMessage = true;
				return;
			}

			if (!this.confirmPassword) {
				this.submissionErrors.push(toSubmissionError(this.confirmPasswordRequiredMessage));
				this.showErrorMessage = true;
				return;
			}

			if (this.newPassword.length < minimumPasswordLength) {
				this.submissionErrors.push(toSubmissionError(this.passwordTooShortMessage));
				this.showErrorMessage = true;
				return;
			}

			if (this.newPassword !== this.confirmPassword) {
				this.submissionErrors.push(toSubmissionError(this.passwordsDoNotMatchMessage));
				this.showErrorMessage = true;
				return;
			}

			const resetPasswordParameters: ResetPasswordParameters = {
				email: this.email,
				newPassword: this.newPassword,
				token: this.token,
				confirmPassword: this.confirmPassword,
			};

			const response = await PasswordResetService.resetPassword({ body: resetPasswordParameters });

			if (response.response.status !== 200 && response.error != null) {
				this.submissionErrors = normalizeErrors(response.error);
				this.showErrorMessage = true;
				this.showSuccessMessage = false;
			} else {
				this.showSuccessMessage = true;
				this.showErrorMessage = false;
				setTimeout(() => {
					window.location.href = this.loginUrl;
				}, 2000);
			}
		},
	};
};
